import global from "./global.js";

const log = global.log.getLogger("f_mtac.Menu");
log.setLevel(log.levels.WARN);

const DEFAULT_CLOSE_TIMER_TIMEOUT = 10000;

let MenuGroup = function () {
    let menus = {};
    Object.defineProperty(this, "currently_opened", {
        set: () => {},
        get: () => {
            for (let name in menus) {
                if (menus[name].opened) {
                    return name;
                }
            }
            return null;
        }
    });
    this.create = function (options) {
        menus[options.name] = new Menu(options);
    };
    this.display = async function (selectors) {
        //first close others menus then open selected menu (AGPOC-1901)
        for (let selector in menus) {
            if (!selectors.includes(selector)) {
                log.debug(`MenuGroup.display: Close ${selector}`);
                await menus[selector].close();
            }
        }
        for (let selector in menus) {
            if (selectors.includes(selector)) {
                if (menus[selector].opened) {
                    continue;
                } else {
                    await menus[selector].open();
                }
            }
        }
    };

    this.close = async function (name) {
        return menus[name].close();
    };

    this.set_close_handler = function (name, handler) {
        if (!(name in menus)) {
            return;
        }
        menus[name].close_handler = handler;
    };

    this.unset_close_timeout = function (name) {
        if (!(name in menus)) {
            return;
        }
        menus[name].unset_close_timeout();
    };

    this.set_close_timeout = function (name) {
        log.debug("MenuGroup.set_close_timeout for", name);
        if (!(name in menus)) {
            return;
        }
        menus[name].set_close_timeout();
    };

    global.event_bus.subscribe("scene.interaction.drag", () => {
        if (this.currently_opened !== null) {
            menus[this.currently_opened].set_close_timeout();
        }
    });
};

let Menu = function ({name, frame_content, open_handler, close_handler, close_timer, close_timer_timeout, contents}) {
    var opened = false;
    var $handle = $("<div/>")
        .attr({
            id: name,
            class: "opacity-optimized"
        })
        .appendTo(window.document.body);

    this.name = name;

    Object.defineProperty(this, "opened", {
        set: () => {},
        get: () => opened
    });

    Object.defineProperty(this, "close_handler", {
        set: (handler) => {
            if (handler === undefined || handler === null) {
                close_handler = () => {};
            } else {
                if (typeof handler !== "function") {
                    throw "wrong close handler type";
                }
                close_handler = handler;
            }
        },
        get: () => close_handler
    });

    Object.defineProperty(this, "open_handler", {
        set: (handler) => {
            if (handler === undefined || handler === null) {
                open_handler = () => {};
            } else {
                if (typeof handler !== "function") {
                    throw "wrong open handler type";
                }
                open_handler = handler;
            }
        },
        get: () => open_handler
    });

    if (close_timer !== undefined) {
        close_timer = null;
    }

    if (close_timer !== undefined && close_timer_timeout === undefined) {
        close_timer_timeout = DEFAULT_CLOSE_TIMER_TIMEOUT;
    }

    this.close_handler = close_handler;
    this.open_handler = open_handler;

    this.unset_close_timeout = function () {
        if (close_timer !== null) {
            log.debug("Menu.unset_close_timeout");
            clearTimeout(close_timer);
            close_timer = null;
        }
    };

    this.set_close_timeout = function () {
        log.debug("Menu.set_close_timeout");
        this.unset_close_timeout();
        close_timer = setTimeout(() => {
            log.debug("Menu.event: Close timer elapsed for", this.name);
            this.close();
            close_timer = null;
        }, close_timer_timeout);
    };

    $handle.on("mouseenter", () => {
        log.debug("Menu.on:mouseenter");
        if (close_timer !== undefined) {
            this.unset_close_timeout();
        }
    });

    $handle.on("mouseleave", () => {
        log.debug("Menu.on:mouseleave");
        if (close_timer !== undefined) {
            this.set_close_timeout();
        }
    });

    this.open = async function () {
        log.debug("Menu.open");
        if (opened) {
            return;
        }
        if ($handle.hasClass("menu-closing") || $handle.hasClass("menu-opening")) {
            return;
        }
        let opening = open_handler();
        $handle.addClass("menu-opening").removeClass("menu-closed");
        await new Promise((resolve) => setTimeout(resolve, 500));
        $handle.removeClass("menu-opening").addClass("menu-opened");
        await opening;
        opened = true;
        if (close_timer !== undefined) {
            this.set_close_timeout();
        }
    };

    this.close = async function () {
        log.debug("Menu.close");
        if (!opened) {
            return;
        }
        if ($handle.hasClass("menu-closing") || $handle.hasClass("menu-opening")) {
            return;
        }
        log.debug("close_timer=", close_timer);
        if (close_timer !== undefined) {
            this.unset_close_timeout();
        }
        let closing = close_handler();
        $handle.addClass("menu-closing").removeClass("menu-opened");
        await new Promise((resolve) => setTimeout(resolve, 500));
        $handle.removeClass("menu-closing").addClass("menu-closed");
        await closing;
        opened = false;
    };

    $handle.html("");
    $handle.addClass("mtac_menu").addClass("menu-closed");

    let menu_top = $("<div />").attr({
        class: "mtac-menu-top"
    });
    if (frame_content !== undefined) {
        menu_top.append(frame_content);
    }
    let menu = $("<div />").attr({
        class: "menu-items"
    });

    let item_list_container = $("<div />")
        .attr({
            class: "menu-items-list"
        })
        .appendTo(menu);

    let item_list = $("<div />")
        .attr({
            class: "menu-btn-group",
            role: "group"
        })
        .appendTo(item_list_container);

    for (let i in contents) {
        let element_id = contents[i][0] === "#" ? contents[i].substr(1) : contents[i];
        $("<div />")
            .attr({
                id: element_id,
                class: "mtac-menu-item"
            })
            .appendTo(item_list);
    }

    let btn_close = $("<button />")
        .attr({
            class: "btn btn-menu-close"
        })
        .appendTo(item_list);
    let close_icon = $("<span />")
        .attr({
            class: "mtac-opacity-100"
        })
        .appendTo(btn_close);
    $("<i />")
        .attr({
            class: "f_mtac-icon-close f_mtac-icon-xlg"
        })
        .appendTo(close_icon);
    btn_close.on("click", () => {
        log.debug("Menu.event: Close button pressed.");
        this.close();
    });

    menu.appendTo($handle);
    menu_top.appendTo($handle);
};

export default new MenuGroup();
